import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Download, FileText } from "lucide-react";
import Layout from "@/components/Layout";
import { api, formatEUR } from "@/lib/api";
import { CommissionHistory, CommissionLines, MotifAction, PaymentForm, StatusPill, STATUS_META } from "@/components/commissions/CommissionParts";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const thisMonth = () => new Date().toISOString().slice(0, 7);

function Kpi({ label, value, testid, accent }) {
  return (
    <div className="card-base p-4" data-testid={testid}>
      <div className="overline">{label}</div>
      <div className={`metric-value text-xl md:text-2xl mt-1 ${accent ? "text-[color:var(--cf-primary-text)]" : "text-slate-900"}`}>{formatEUR(value)}</div>
    </div>
  );
}

function CommissionRow({ c, onChange }) {
  const [open, setOpen] = useState(false);
  const [paying, setPaying] = useState(false);
  const payable = ["acquise", "partiellement_payee"].includes(c.status);
  return (
    <div className="card-base p-4" data-testid={`commission-row-${c.dossier_id}`}>
      <button type="button" className="w-full flex flex-wrap items-center gap-x-4 gap-y-1 text-left" onClick={() => setOpen(!open)} data-testid={`commission-toggle-${c.dossier_id}`}>
        <div className="flex-1 min-w-[180px]">
          <div className="font-bold text-slate-900 text-[14px]">{c.dossier_nom}</div>
          <div className="text-[12px] text-slate-500">{c.base?.marketplace ? "Marketplace · " : ""}{c.lines.length} ligne(s) · signé {c.signed_at || "—"}{c.acquired_at ? ` · encaissé ${c.acquired_at}` : c.encaissement_previsionnel_month ? ` · encaissement prévu ${c.encaissement_previsionnel_month}` : ""}</div>
        </div>
        <StatusPill status={c.status} testid={`commission-status-${c.dossier_id}`} />
        <div className="text-right"><div className="font-bold tabular-nums text-slate-900">{formatEUR(c.total)}</div><div className="text-[11px] text-slate-500 tabular-nums">payé {formatEUR(c.paid_total)} · reste {formatEUR(c.reste_a_payer)}</div></div>
      </button>
      {open && (
        <div className="mt-4 space-y-4 border-t border-slate-100 pt-4">
          <CommissionLines commission={c} testid={`commission-lines-${c.dossier_id}`} />
          <div className="text-[12px] text-slate-500" data-testid={`commission-base-${c.dossier_id}`}>
            Base : CAPP {formatEUR(c.base.ca_brut_total)} · taux {c.base.taux_remuneration_pct} % · rétro apporteur {formatEUR(c.base.retro_apporteur)} · rétro parrain {formatEUR(c.base.retro_parrain)}{c.base.marketplace ? ` · split structure ${c.base.split_structure_pct} % sur honoraires` : ""} · {c.frozen ? `valeurs figées le ${(c.frozen_at || "").slice(0, 10)}` : "valeurs recalculées tant que non acquise"}
          </div>
          {payable && !paying && <button type="button" className="btn-primary" onClick={() => setPaying(true)} data-testid={`commission-pay-btn-${c.dossier_id}`}>Enregistrer un paiement</button>}
          {payable && paying && <PaymentForm commission={c} onDone={(nc) => { setPaying(false); onChange(nc); }} onCancel={() => setPaying(false)} />}
          {c.status === "previsionnelle" && <div className="text-[12px] text-slate-500" data-testid={`commission-not-acquired-${c.dossier_id}`}>Paiement possible après saisie de la date d'encaissement sur le dossier.</div>}
          {(c.payments || []).filter((p) => !p.cancelled).length > 0 && (
            <ul className="text-[12.5px] space-y-1" data-testid={`commission-payments-${c.dossier_id}`}>
              {c.payments.filter((p) => !p.cancelled).map((p) => (
                <li key={p.id} className="flex items-center gap-2"><span className="tabular-nums text-slate-500">{p.date}</span><span className="font-semibold">{formatEUR(p.montant)}</span><span className="text-slate-500">→ {p.beneficiaire}{p.reference ? ` · ${p.reference}` : ""} · saisi par {p.recorded_by}</span>
                  <MotifAction label="Annuler" endpoint={`/commissions/${c.id}/paiements/${p.id}/annuler`} onDone={onChange} testid={`payment-cancel-${p.id}`} tone="danger" /></li>
              ))}
            </ul>
          )}
          <div className="flex flex-wrap gap-2">
            {c.status !== "contestee" ? <MotifAction label="Contester" endpoint={`/commissions/${c.id}/contester`} onDone={onChange} testid={`commission-contest-${c.dossier_id}`} tone="danger" />
              : <MotifAction label="Lever la contestation" endpoint={`/commissions/${c.id}/lever-contestation`} onDone={onChange} testid={`commission-uncontest-${c.dossier_id}`} />}
            {c.frozen && <MotifAction label="Recalculer (correction motivée)" endpoint={`/commissions/${c.id}/recalculer`} onDone={onChange} testid={`commission-recalc-${c.dossier_id}`} />}
            <Link to={`/dossier/${c.dossier_id}`} className="btn-ghost" data-testid={`commission-open-dossier-${c.dossier_id}`}>Ouvrir le dossier</Link>
          </div>
          <CommissionHistory commission={c} />
        </div>
      )}
    </div>
  );
}

export default function Commissions() {
  const [data, setData] = useState(null);
  const [filters, setFilters] = useState({ statut: "", mois: "", origine: "" });
  const [pdfMonth, setPdfMonth] = useState(thisMonth());
  const load = useCallback(async () => {
    const r = await api.get("/commissions", { params: Object.fromEntries(Object.entries(filters).filter(([, v]) => v)) });
    setData(r.data);
  }, [filters]);
  useEffect(() => { load(); }, [load]);
  const replace = () => load();
  const qs = new URLSearchParams(Object.fromEntries(Object.entries(filters).filter(([, v]) => v))).toString();
  const k = data?.kpis;
  return (
    <Layout title="Commissions" subtitle="Acquises à l'encaissement effectif · paiements enregistrés, jamais déclenchés" backTo="/">
      <div className="space-y-5" data-testid="commissions-page">
        {k && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Kpi label="Acquis" value={k.acquis} testid="kpi-acquis" accent /><Kpi label="Payé" value={k.paye} testid="kpi-paye" /><Kpi label="Reste à payer" value={k.reste_a_payer} testid="kpi-reste" /><Kpi label="Prévisionnel" value={k.previsionnel} testid="kpi-previsionnel" />
          </div>
        )}
        <div className="card-base p-4 flex flex-wrap items-end gap-3" data-testid="commissions-filters">
          <label className="text-xs text-slate-500">Statut
            <select className="input-base block mt-1" value={filters.statut} onChange={(e) => setFilters({ ...filters, statut: e.target.value })} data-testid="filter-statut">
              <option value="">Tous</option>{Object.entries(STATUS_META).map(([k2, m]) => <option key={k2} value={k2}>{m.label}</option>)}
            </select>
          </label>
          <label className="text-xs text-slate-500">Mois
            <input type="month" className="input-base block mt-1" value={filters.mois} onChange={(e) => setFilters({ ...filters, mois: e.target.value })} data-testid="filter-mois" />
          </label>
          <label className="text-xs text-slate-500">Origine
            <select className="input-base block mt-1" value={filters.origine} onChange={(e) => setFilters({ ...filters, origine: e.target.value })} data-testid="filter-origine">
              <option value="">Toutes</option><option value="marketplace">Marketplace</option><option value="apporteur">Apporteur / parrain</option>
            </select>
          </label>
          <div className="flex-1" />
          <a className="btn-secondary" href={`${API}/commissions/export.csv${qs ? `?${qs}` : ""}`} data-testid="export-csv-btn"><Download size={14} /> CSV (;)</a>
          <div className="flex items-end gap-1">
            <input type="month" className="input-base" value={pdfMonth} onChange={(e) => setPdfMonth(e.target.value)} data-testid="pdf-month-input" />
            <a className="btn-secondary" href={`${API}/commissions/export.pdf?mois=${pdfMonth}`} data-testid="export-pdf-btn"><FileText size={14} /> PDF mensuel</a>
          </div>
        </div>
        {!data ? <div className="text-sm text-slate-400 animate-pulse">Chargement…</div>
          : data.items.length === 0 ? <div className="card-base p-8 text-center text-sm text-slate-500" data-testid="commissions-empty">Aucune commission : elles apparaissent dès qu'un dossier est signé.</div>
          : <div className="space-y-2" data-testid="commissions-list">{data.items.map((c) => <CommissionRow key={c.id} c={c} onChange={replace} />)}</div>}
      </div>
    </Layout>
  );
}
